import axios, { AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { getToken } from './services/authService';
import { clearAuthToken } from './tokenStore';

// Create axios instance with base configuration
const axiosInstance = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_URL || 'https://api.lovingecards.com/api/v1',
  timeout: 30000, // 30 seconds timeout
  // Skip axios's env-proxy lookup on the server: it goes through proxy-from-env,
  // which calls Node's deprecated url.parse() (DEP0169 warning). No proxy is used.
  proxy: false,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add JWT token
axiosInstance.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    try {
      // Skip token authentication for server-side rendering
      if (typeof window === 'undefined') {
        // Allow public API calls during SSR (category data, etc.)
        return config;
      }
      
      // Get token from authService for client-side requests
      const token = await getToken();
      
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      
      return config;
    } catch (error) {
      // console.error('Error in request interceptor:', error);
      return config;
    }
  },
  (error: AxiosError) => {
    // console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors and 401 and 403 responses
axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => {
      return response;
    },
    async (error: AxiosError) => {
      const originalRequest = error.config;

      // Handle 401 Unauthorized or 403 Forbidden responses
      if (error.response?.status === 401 || error.response?.status === 403) {
        const status = error.response.status;

        // Clear token from cache and localStorage
        if (typeof window !== 'undefined') {
          clearAuthToken();

          try {
            const { signOut } = await import('next-auth/react');
            await signOut({ redirect: false });
          } catch (e) {
            // ignore error
          }

          const currentPath = window.location.pathname;

          // For 403, only redirect to login if we are on /admin or /account routes
          // For 401, always redirect to login (restoring previous behavior)
          const shouldRedirect = status === 401 || (status === 403 && (currentPath.startsWith('/admin') || currentPath.startsWith('/account')));

          // Public pages (contributors signing, recipients viewing, guests RSVPing) must
          // never be bounced to login just because an anonymous visitor carried a stale
          // token — the token is cleared above, so we only reject the promise there.
          const PUBLIC_PATH_PREFIXES = [
            '/card/',
            '/invitation/',
            '/video/',
            '/videos/contribute',
            '/group-video/',
            '/group-video-cards/group-videos/contribute',
          ];
          const isPublicPath = PUBLIC_PATH_PREFIXES.some((prefix) => currentPath.startsWith(prefix));

          // Don't bounce auth pages back to themselves
          if (shouldRedirect && !isPublicPath && !currentPath.startsWith('/auth/')) {
            // Preserve the full return URL (path + query) so the user lands exactly where they were
            const returnUrl = `${currentPath}${window.location.search}`;
            sessionStorage.setItem('redirectAfterLogin', returnUrl);
            window.location.href = `/auth/login?callbackUrl=${encodeURIComponent(returnUrl)}`;
          }
        }
      }

      return Promise.reject(error);
    }
);

export default axiosInstance;